import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Alert,
  Button,
  Pagination,
  IconButton,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldIcon from '@mui/icons-material/Shield';
import WarningIcon from '@mui/icons-material/Warning';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import PokemonSprite from '../PokemonSprite';
import { getPokemonDetailPath } from '../../utils/navigationHelper';

/**
 * Componente para mostrar sugerencias de Pokémon o equipos optimizados
 * Muestra análisis detallado, cobertura de tipos, y recomendaciones
 */
const TeamSuggestions = ({ 
  suggestions = [], 
  mode = 'suggest', // 'suggest' o 'optimize'
  loading = false,
  onAddPokemon
}) => {
  const [expandedAccordions, setExpandedAccordions] = useState({ 0: true });
  const [page, setPage] = useState(1);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const itemsPerPage = 6;

  // Reset pagination when suggestions change
  useEffect(() => {
    setPage(1);
  }, [suggestions]);

  const getTypeColor = (typeName) => {
    const typeColors = {
      normal: '#A8A878', fire: '#F08030', water: '#6890F0',
      electric: '#F8D030', grass: '#78C850', ice: '#98D8D8',
      fighting: '#C03028', poison: '#A040A0', ground: '#E0C068',
      flying: '#A890F0', psychic: '#F85888', bug: '#A8B820',
      rock: '#B8A038', ghost: '#705898', dragon: '#7038F8',
      dark: '#705848', steel: '#B8B8D0', fairy: '#EE99AC'
    };
    return typeColors[typeName?.toLowerCase()] || '#777';
  };

  if (loading) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ color: 'white', mb: 2, textAlign: 'center' }}>
          Analyzing {mode === 'suggest' ? 'suggestions' : 'team combinations'}...
        </Typography>
        <LinearProgress sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      </Box>
    );
  }

  if (!suggestions || suggestions.length === 0) {
    return (
      <Alert severity="info">
        No suggestions available. {mode === 'suggest' ? 'Select some Pokémon to get started.' : 'Select at least 6 Pokémon to optimize.'}
      </Alert>
    );
  }

  // Modo: Sugerencias de Pokémon individuales (< 6)
  if (mode === 'suggest') {
    const startIndex = (page - 1) * itemsPerPage;
    const currentSuggestions = suggestions.slice(startIndex, startIndex + itemsPerPage);
    const totalPages = Math.ceil(suggestions.length / itemsPerPage);

    return (
      <Box>
        <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
          <TrendingUpIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Suggested Pokémon
        </Typography>

        <Grid container spacing={2}>
          {currentSuggestions.map((suggestion, index) => {
            // Calculamos el índice real para el diseño del 'Top 1'
            const realIndex = startIndex + index;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={realIndex}>
                <Card 
                  sx={{ 
                    backgroundColor: 'rgba(30, 30, 30, 0.9)',
                    border: realIndex === 0 ? '2px solid #24cc9f' : '1px solid rgba(255,255,255,0.1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      {/* Sprite */}
                      <Box sx={{ width: 60, height: 60, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PokemonSprite 
                          pokemon={suggestion.pokemon}
                          style={{ maxWidth: '100%', maxHeight: '100%' }}
                        />
                      </Box>

                      {/* Info */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold', lineHeight: 1.2 }}>
                            {suggestion.pokemon.displayName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                            #{suggestion.pokemon.id}
                          </Typography>
                          {realIndex === 0 && (
                            <Chip 
                              label="Top" 
                              size="small" 
                              color="primary" 
                              sx={{ height: 20, fontSize: '0.7rem', ml: 'auto' }}
                            />
                          )}
                        </Box>

                        {/* Tipos */}
                        {suggestion.pokemon.types && (
                          <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                            {suggestion.pokemon.types.map((typeObj) => (
                              <Chip
                                key={typeObj.name}
                                label={typeObj.name}
                                size="small"
                                sx={{
                                  backgroundColor: getTypeColor(typeObj.name),
                                  color: 'white',
                                  fontWeight: 'bold',
                                  height: 20,
                                  fontSize: '0.7rem'
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Score y Razones */}
                    <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#24cc9f', fontWeight: 'bold' }}>
                          Score: {suggestion.score.toFixed(1)}
                        </Typography>
                        {onAddPokemon && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            onClick={() => onAddPokemon(suggestion.pokemon)}
                            sx={{ 
                              fontSize: '0.65rem', 
                              padding: '2px 6px',
                              minWidth: 'auto',
                              borderColor: 'rgba(36, 204, 159, 0.5)',
                              color: '#24cc9f',
                              '&:hover': {
                                borderColor: '#24cc9f',
                                backgroundColor: 'rgba(36, 204, 159, 0.1)'
                              }
                            }}
                          >
                            Add +
                          </Button>
                        )}
                      </Box>

                      {suggestion.reasoning && suggestion.reasoning.length > 0 && (
                        <List dense sx={{ py: 0 }}>
                          {suggestion.reasoning.map((reason, idx) => (
                            <ListItem 
                              key={idx} 
                              sx={{ 
                                py: 0.25, 
                                px: 0,
                                '&:hover': { backgroundColor: 'transparent' }
                              }}
                            >
                              <ListItemText
                                primary={`• ${reason}`}
                                primaryTypographyProps={{
                                  variant: 'caption',
                                  sx: { color: 'rgba(255,255,255,0.8)', lineHeight: 1.2 }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={(e, value) => {
                setPage(value);
              }} 
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                },
                '& .Mui-selected': {
                  backgroundColor: 'rgba(36, 204, 159, 0.2) !important',
                  borderColor: '#24cc9f',
                }
              }}
            />
          </Box>
        )}
      </Box>
    );
  }

  // Modo: Equipos optimizados (≥ 6)
  const handleCopyPokepaste = (team, teamIndex, event) => {
    event.stopPropagation();
    const text = team.map(p => p.displayName || p.name).join('\n\n');
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(teamIndex);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  const handleAccordionChange = (panelIndex) => (event, isExpanded) => {
    setExpandedAccordions(prev => ({
      ...prev,
      [panelIndex]: isExpanded
    }));
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
        <ShieldIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
        Optimized Team Combinations
      </Typography>

      {suggestions.map((teamData, teamIndex) => {
        const { team, score, analysis } = teamData;
        const coverage = teamData.teamCoverage || teamData.coverage;

        const isExpanded = !!expandedAccordions[teamIndex];

        return (
          <Accordion 
            key={teamIndex}
            expanded={isExpanded}
            onChange={handleAccordionChange(teamIndex)}
            sx={{ 
              mb: 2,
              backgroundColor: 'rgba(30, 30, 30, 0.9)',
              border: teamIndex === 0 ? '2px solid #24cc9f' : '1px solid rgba(255,255,255,0.1)',
              overflow: 'hidden',
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
              sx={{ 
                px: { xs: 1.25, sm: 2 },
                '& .MuiAccordionSummary-content': { 
                  alignItems: 'center',
                  m: 0,
                  minWidth: 0
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0, gap: { xs: 1, sm: 2 }, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', width: { xs: 'auto', sm: '80px' }, flexShrink: 0 }}>
                  Team #{teamIndex + 1}
                </Typography>
                
                {/* Previsualización de Sprites SOLO cuando está contraído */}
                {!isExpanded && (
                  <Box sx={{ display: 'flex', flex: 1, minWidth: 0, gap: 1, overflow: 'hidden', order: { xs: 3, sm: 0 }, width: { xs: '100%', sm: 'auto' } }}>
                    {team.map((pokemon, idx) => (
                      <Box key={idx} sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                         <PokemonSprite pokemon={pokemon} style={{ maxWidth: '100%', maxHeight: '100%' }} />
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={{ ml: { xs: 'auto', sm: 'auto' }, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, order: { xs: 2, sm: 0 }, flexShrink: 0 }}>
                  {teamIndex === 0 && (
                    <Chip
                      label="Best"
                      size="small"
                      color="primary"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#24cc9f',
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Score: {score}
                  </Typography>
                  <Tooltip title={copiedIndex === teamIndex ? 'Copied!' : 'Copy paste'}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleCopyPokepaste(team, teamIndex, e)}
                      sx={{ color: copiedIndex === teamIndex ? '#24cc9f' : 'rgba(255,255,255,0.5)' }}
                    >
                      {copiedIndex === teamIndex
                        ? <CheckIcon fontSize="small" />
                        : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {/* Pokémon del equipo */}
              <Typography variant="subtitle2" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                Team Members:
              </Typography>
              <Grid container spacing={1} sx={{ mb: 2 }}>
                {team.map((pokemon, idx) => (
                  <Grid item xs={4} sm={2} key={idx}>
                    <Card
                      onClick={() => {
                        const detailPath = getPokemonDetailPath(pokemon);
                        if (!detailPath) return;
                        window.open(detailPath, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{
                        backgroundColor: 'rgba(50, 50, 50, 0.5)',
                        textAlign: 'center',
                        p: 1,
                        cursor: 'pointer',
                        border: '1px solid transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#24CCFF',
                          boxShadow: '0 0 12px rgba(36, 204, 255, 0.35)',
                          backgroundColor: 'rgba(36, 204, 255, 0.08)'
                        }
                      }}
                    >
                      <Box sx={{ height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <PokemonSprite 
                          pokemon={pokemon}
                          style={{ maxWidth: '100%', maxHeight: '100%' }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'white', display: 'block', mt: 0.5 }}>
                        {pokemon.displayName}
                      </Typography>
                      {pokemon.types && (
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.25, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {pokemon.types.map((typeObj) => (
                            <Chip
                              key={typeObj.name}
                              label={typeObj.name}
                              size="small"
                              sx={{
                                backgroundColor: getTypeColor(typeObj.name),
                                color: 'white',
                                fontSize: '0.6rem',
                                height: 16,
                                '& .MuiChip-label': { px: 0.5 }
                              }}
                            />
                          ))}
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />

              {/* Análisis del equipo */}
              {analysis && (
                <Box>
                  {/* Fortalezas */}
                  {analysis.strengths && analysis.strengths.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#24cc9f', mb: 1, fontWeight: 'bold' }}>
                        <TrendingUpIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.5 }} />
                        Strengths:
                      </Typography>
                      <List dense>
                        {analysis.strengths.map((strength, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemText
                              primary={`✓ ${strength}`}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { color: 'rgba(255,255,255,0.9)' }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Debilidades */}
                  {analysis.weaknesses && analysis.weaknesses.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#ff6b6b', mb: 1, fontWeight: 'bold' }}>
                        <WarningIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.5 }} />
                        Weaknesses:
                      </Typography>
                      <List dense>
                        {analysis.weaknesses.map((weakness, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemText
                              primary={`⚠ ${weakness}`}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { color: 'rgba(255,255,255,0.9)' }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Recomendaciones */}
                  {analysis.recommendations && analysis.recommendations.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#ffd93d', mb: 1, fontWeight: 'bold' }}>
                        💡 Recommendations:
                      </Typography>
                      <List dense>
                        {analysis.recommendations.map((rec, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'transparent' } }}>
                            <ListItemText
                              primary={`• ${rec}`}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { color: 'rgba(255,255,255,0.9)' }
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Balance ofensivo detallado */}
                  {analysis.offensiveBreakdown && (
                    <Box sx={{ mt: analysis.recommendations?.length ? 2 : 0 }}>
                      <Typography variant="subtitle2" sx={{ color: '#8dd3ff', mb: 1, fontWeight: 'bold' }}>
                        ⚔️ Offensive breakdown:
                      </Typography>
                      <List dense>
                        <ListItem sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'transparent' } }}>
                          <ListItemText
                            primary={`Physical: ${analysis.offensiveBreakdown.physical?.length ? analysis.offensiveBreakdown.physical.join(', ') : 'None'}`}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'rgba(255,255,255,0.9)' }
                            }}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'transparent' } }}>
                          <ListItemText
                            primary={`Special: ${analysis.offensiveBreakdown.special?.length ? analysis.offensiveBreakdown.special.join(', ') : 'None'}`}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'rgba(255,255,255,0.9)' }
                            }}
                          />
                        </ListItem>
                        <ListItem sx={{ py: 0.5, px: 2, '&:hover': { backgroundColor: 'transparent' } }}>
                          <ListItemText
                            primary={`Mixed: ${analysis.offensiveBreakdown.mixed?.length ? analysis.offensiveBreakdown.mixed.join(', ') : 'None'}`}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { color: 'rgba(255,255,255,0.9)' }
                            }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  )}
                </Box>
              )}

              {/* Cobertura de tipos */}
              {coverage && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }} />
                  <Typography variant="subtitle2" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                    Type Coverage:
                  </Typography>
                  
                  {/* Tipos presentes */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                      Types: {coverage.types.length}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {coverage.types.map((type) => (
                        <Chip
                          key={type}
                          label={type}
                          size="small"
                          sx={{
                            backgroundColor: getTypeColor(type),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default TeamSuggestions;
